import { LocationPair, SimulationActivityType } from './simulation.util';
import { LOCAL_FULL_SCENARIO_COORDINATES } from './local-simulation.util';

export interface SimulationScenario {
  id: string;
  label: string;
  title: string;
  summary: string;
  see: string;
  why: string;
  survivorLocations: LocationPair[];
  perpetratorLocations: LocationPair[];
  /**
   * When set, every simulated check-in in this scenario sends this activity
   * (confidence 90) so API `decideCheckInTimeForActivity` can fire for
   * RUNNING / IN_VEHICLE / ON_BICYCLE.
   */
  activity?: SimulationActivityType;
}

export const SIMULATION_SCENARIOS: SimulationScenario[] = [
  {
    id: "full-scenario-local",
    label: "Full Scenario (Offline / Local) — All Use Cases",
    title: "Full Proximity Lifecycle (All Use Cases)",
    summary:
      "A complete offline demonstration replaying all proximity lifecycle use cases: outside monitoring, approaching through Yellow, breach into Red, boundary loitering within hysteresis buffer, step down to Yellow, and safe departure on a new exit route far outside buffer zone. Handled purely locally without real check-in HTTP requests or WebSockets.",
    see:
      "Smooth continuous marker movement across 108 waypoints starting from cleared area outside the buffer zone (~2.4 km). Watch faces display dynamic check-in cadence (cleared: 30s, buffer: 7s, yellow: 7s, red: 3s), always alert while in Yellow and Red zones, clear in the buffer zone, and notify Survivor with Notice: All Clear upon exiting the 2× Yellow buffer zone along a distinct outbound path.",
    why:
      "Enables advocates, stakeholders, and developers to showcase and verify the entire system offline reliably with smooth continuous tracking.",
    survivorLocations: LOCAL_FULL_SCENARIO_COORDINATES.survivor,
    perpetratorLocations: LOCAL_FULL_SCENARIO_COORDINATES.perpetrator,
  },
  {
    id: "custom-draggable",
    label: "Custom User Position Draggable",
    title: "Custom User Position Draggable",
    summary: "Drag simulated user markers on the map. Releasing a marker immediately sends a check-in.",
    see: "Real-time recalculation of separation distance, zone alerts on watch faces, and advocate event logs on each drop.",
    why: "Interactive manual testing of proximity alert thresholds and check-in processing without waiting for automated routes.",
    survivorLocations: [
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7725649, 35.3712677],
    ],
  },
  {
    id: "activity-running-close-in",
    label: "RUNNING activity — approach through Yellow into Red",
    title: "RUNNING activity — approach through Yellow into Red",
    summary:
      "Perpetrator closes from outside through Yellow into Red while check-ins report RUNNING (confidence 90).",
    see:
      "When Yellow/Red pair check-in times are above 7s, the returned CHECK_IN_TIME drops to 7s once those zones are entered (decideCheckInTimeForActivity). Watch the bottom-panel Check-in time label.",
    why:
      "Exercises activity override in yellow/buffer and red: if zone check-in time > 7s, activity forces 7s.",
    activity: "RUNNING",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7625467, 35.3756116],
      [-80.7668912, 35.3737278],
      [-80.7712356, 35.3718441],
      [-80.7755801, 35.3699604],
      [-80.7761636, 35.3697073],
      [-80.7769418, 35.3693699],
      [-80.7777199, 35.3690325],
      [-80.778498, 35.3686952],
      [-80.7792761, 35.3683578],
      [-80.7800542, 35.3680204],
      [-80.7808323, 35.367683],
      [-80.7811241, 35.3675565],
      [-80.7814321, 35.3674229],
      [-80.7817401, 35.3672894],
      [-80.7820481, 35.3671558],
      [-80.7823561, 35.3670223],
      [-80.7826641, 35.3668887],
      [-80.7829721, 35.3667552],
    ],
  },
  {
    id: "activity-running-yellow-hold",
    label: "RUNNING activity — hold inside Yellow",
    title: "RUNNING activity — hold inside Yellow",
    summary:
      "Perpetrator stays inside the Yellow ring while check-ins report RUNNING (confidence 90).",
    see:
      "If the pair's yellowCheckInTime is greater than 7s, CHECK_IN_TIME should become 7s and stay there while activity remains RUNNING.",
    why:
      "Isolates the yellow/buffer branch of decideCheckInTimeForActivity without a zone transition.",
    activity: "RUNNING",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
    ],
  },
  {
    id: "activity-bicycle-red-hold",
    label: "ON_BICYCLE activity — hold inside Red",
    title: "ON_BICYCLE activity — hold inside Red",
    summary:
      "Perpetrator stays inside the Red ring while check-ins report ON_BICYCLE (confidence 90).",
    see:
      "If the pair's redCheckInTime is greater than 7s, CHECK_IN_TIME should become 7s for the Red activity override.",
    why:
      "Isolates the red branch of decideCheckInTimeForActivity.",
    activity: "ON_BICYCLE",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7845, 35.36549],
      [-80.7845, 35.36549],
      [-80.7845, 35.36549],
      [-80.7845, 35.36549],
      [-80.7845, 35.36549],
      [-80.7845, 35.36549],
      [-80.7845, 35.36549],
      [-80.7845, 35.36549],
      [-80.7845, 35.36549],
      [-80.7845, 35.36549],
    ],
  },
  {
    id: "activity-vehicle-outside-approach",
    label: "IN_VEHICLE activity — approaching from outside (ETA ≤ 10 min)",
    title: "IN_VEHICLE activity — approaching from outside (ETA ≤ 10 min)",
    summary:
      "Perpetrator approaches from outside the Yellow ring with IN_VEHICLE activity so closing ETA to the buffer is under 10 minutes.",
    see:
      "Even while still cleared/outside, CHECK_IN_TIME should drop to 7s when direction is approaching and ETA to the Yellow threshold is ≤ 10 minutes.",
    why:
      "Exercises the cleared + approaching branch of decideCheckInTimeForActivity.",
    activity: "IN_VEHICLE",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    // Start just outside a typical ~400–1000m Yellow ring and close quickly (vehicle-scale steps).
    perpetratorLocations: [
      [-80.7705, 35.3722],
      [-80.7718, 35.3716],
      [-80.7731, 35.3710],
      [-80.7744, 35.3704],
      [-80.7757, 35.3698],
      [-80.7770, 35.3692],
      [-80.7783, 35.3686],
      [-80.7796, 35.3680],
      [-80.7809, 35.3674],
      [-80.7822, 35.3668],
      [-80.7835, 35.3662],
      [-80.7845, 35.3658],
    ],
  },
  {
    id: "activity-running-outside-approach",
    label: "RUNNING activity — approaching from outside (ETA ≤ 10 min)",
    title: "RUNNING activity — approaching from outside (ETA ≤ 10 min)",
    summary:
      "Perpetrator approaches from well outside the buffer with RUNNING activity (confidence 90). Survivor also moves ~5–10 m between check-ins. Many early check-ins stay cleared so the ETA ≤ 10 min override can be observed before Yellow/Red.",
    see:
      "While still cleared/outside the buffer, CHECK_IN_TIME should drop to 7s when direction is approaching and ETA to the Yellow threshold is ≤ 10 minutes. Later points enter buffer/Yellow.",
    why:
      "Same cleared + approaching branch of decideCheckInTimeForActivity, with extra cleared-zone samples before the buffer and slight Survivor motion for relative velocity.",
    activity: "RUNNING",
    // Survivor drifts ~5–10 m per check-in near the original anchor.
    survivorLocations: [
      [-80.7858514, 35.3655445],
      [-80.7857864, 35.3655538],
      [-80.7857297, 35.3654988],
      [-80.7857485, 35.3654566],
      [-80.7858417, 35.3654289],
      [-80.7859008, 35.3654693],
      [-80.7858782, 35.36552],
      [-80.785768, 35.36552],
      [-80.7857405, 35.3654811],
      [-80.7857845, 35.3654188],
      [-80.7858616, 35.3654188],
      [-80.7858947, 35.3654655],
      [-80.785831, 35.3655275],
      [-80.7857767, 35.3655197],
      [-80.7857466, 35.3654521],
      [-80.7857962, 35.3654039],
      [-80.7858612, 35.3654133],
      [-80.7858989, 35.3654977],
      [-80.7858567, 35.3655266],
      [-80.7857739, 35.365502],
      [-80.7857605, 35.3654401],
      [-80.7858112, 35.3654054],
      [-80.7859043, 35.3654331],
      [-80.7859139, 35.3654773],
      [-80.7858986, 35.3655481],
      [-80.7858261, 35.3655696],
      [-80.7857755, 35.365535],
      [-80.7857908, 35.3654642],
    ],
    // ~17 cleared samples (>~1000m for a 500m Yellow threshold), then buffer → Yellow.
    perpetratorLocations: [
      [-80.762, 35.376],
      [-80.76283, 35.37562],
      [-80.76367, 35.37524],
      [-80.7645, 35.37487],
      [-80.76533, 35.37449],
      [-80.76617, 35.37411],
      [-80.767, 35.37373],
      [-80.76783, 35.37336],
      [-80.76867, 35.37298],
      [-80.7695, 35.3726],
      [-80.77033, 35.37222],
      [-80.77117, 35.37184],
      [-80.772, 35.37147],
      [-80.77283, 35.37109],
      [-80.77367, 35.37071],
      [-80.7745, 35.37033],
      [-80.77533, 35.36996],
      [-80.77617, 35.36958],
      [-80.777, 35.3692],
      [-80.77783, 35.36882],
      [-80.77867, 35.36844],
      [-80.7795, 35.36807],
      [-80.78033, 35.36769],
      [-80.78117, 35.36731],
      [-80.782, 35.36693],
      [-80.78283, 35.36656],
      [-80.78367, 35.36618],
      [-80.7845, 35.3658],
    ],
  },
  {
    id: "activity-walking-close-in-control",
    label: "WALKING activity — approach (control, no 7s override)",
    title: "WALKING activity — approach (control, no 7s override)",
    summary:
      "Same closing path as the RUNNING scenario, but activity is WALKING so the activity-based 7s override must not apply.",
    see:
      "Check-in time should follow normal Yellow/Red pair cadence (e.g. 20s / 10s), not force 7s from decideCheckInTimeForActivity.",
    why:
      "Negative control: only RUNNING / IN_VEHICLE / ON_BICYCLE with confidence > 50 trigger the activity path.",
    activity: "WALKING",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7625467, 35.3756116],
      [-80.7668912, 35.3737278],
      [-80.7712356, 35.3718441],
      [-80.7755801, 35.3699604],
      [-80.7761636, 35.3697073],
      [-80.7769418, 35.3693699],
      [-80.7777199, 35.3690325],
      [-80.778498, 35.3686952],
      [-80.7792761, 35.3683578],
      [-80.7800542, 35.3680204],
      [-80.7808323, 35.367683],
      [-80.7811241, 35.3675565],
      [-80.7814321, 35.3674229],
      [-80.7817401, 35.3672894],
      [-80.7820481, 35.3671558],
      [-80.7823561, 35.3670223],
      [-80.7826641, 35.3668887],
      [-80.7829721, 35.3667552],
    ],
  },
  {
    id: "location-missing",
    label: "Survivor's location goes missing while the Perpetrator moves",
    title: "No trusted Survivor point",
    summary: "The Subject keeps moving, but the Survivor's device stops reporting a usable fix.",
    see: "No separation figure, no rings and no assumed zone. The map says the location is unusable.",
    why: "Rings are measured from the Survivor. Without that point, any distance would be fiction.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7693552, 35.3726594],
      [-80.7725649, 35.3712677],
      [-80.7757746, 35.369876],
      [-80.7763582, 35.369623],
      [-80.777266, 35.3692294],
      [-80.7781738, 35.3688357],
      [-80.7790815, 35.3684421],
      [-80.7794706, 35.3682734],
      [-80.7798921, 35.3680907],
      [-80.7803136, 35.3679079],
      [-80.780735, 35.3677252],
      [-80.7811565, 35.3675424],
      [-80.781578, 35.3673597],
      [-80.7819995, 35.3671769],
    ],
  },
  {
    id: "yellow-off",
    label: "Survivor's Yellow alert is switched off for this pair",
    title: "Yellow zone stays silent for the Survivor",
    summary: "The Perpetrator approaches through the Yellow ring, but the Survivor's Yellow push is off.",
    see: "The Survivor's watch stays black until the Red ring is crossed. The event log still records the Yellow entry.",
    why: "Shows what a Survivor who opted out of Yellow alerts does and does not receive.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.764492, 35.3747681],
      [-80.768188, 35.3731655],
      [-80.771884, 35.3715629],
      [-80.7755801, 35.3699604],
      [-80.7761636, 35.3697073],
      [-80.7769418, 35.3693699],
      [-80.7777199, 35.3690325],
      [-80.778498, 35.3686952],
      [-80.7792761, 35.3683578],
      [-80.7800542, 35.3680204],
      [-80.7808323, 35.367683],
      [-80.7811241, 35.3675565],
      [-80.7813835, 35.367444],
      [-80.7816428, 35.3673316],
      [-80.7819022, 35.3672191],
      [-80.7821616, 35.3671066],
      [-80.7824209, 35.3669942],
      [-80.7826803, 35.3668817],
    ],
  },
  {
    id: "closing-in",
    label: "Perpetrator closes in on the Survivor",
    title: "Threat is closing in",
    summary: "The separation shrinks from outside the rings, through Yellow, into Red.",
    see: "Rings appear around the Survivor and the separation figure counts down as each ring is crossed.",
    why: "Shows how the check-in cadence tightens as the distance drops.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7625467, 35.3756116],
      [-80.7668912, 35.3737278],
      [-80.7712356, 35.3718441],
      [-80.7755801, 35.3699604],
      [-80.7761636, 35.3697073],
      [-80.7769418, 35.3693699],
      [-80.7777199, 35.3690325],
      [-80.778498, 35.3686952],
      [-80.7792761, 35.3683578],
      [-80.7800542, 35.3680204],
      [-80.7808323, 35.367683],
      [-80.7811241, 35.3675565],
      [-80.7814321, 35.3674229],
      [-80.7817401, 35.3672894],
      [-80.7820481, 35.3671558],
      [-80.7823561, 35.3670223],
      [-80.7826641, 35.3668887],
      [-80.7829721, 35.3667552],
    ],
  },
  {
    id: "stationary-nearby",
    label: "Perpetrator stays nearby without moving",
    title: "Stationary nearby",
    summary: "The Perpetrator stops inside a ring and holds still.",
    see: "The separation stays flat and alerts repeat at that ring's cadence, marked as stationary.",
    why: "Checks that repeated alerts stay steady and are not mistaken for movement.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
      [-80.7814837, 35.36549],
    ],
  },
  {
    id: "approach-leave",
    label: "Perpetrator approaches, then leaves the area completely",
    title: "Perpetrator approaches, then leaves the area completely",
    summary: "Same approach as above, but after reaching about 380 m the Perpetrator turns around and walks well past the Yellow Zone boundary.",
    see: "Yellow, then Red, then back to Yellow, then silence. No all-clear message is sent to either watch — the alerts simply stop and the check-in speed returns to 30 seconds.",
    why: "Proves a clean exit resets everything and shows that watch silence after exit is intended, not a missing notification.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7625467, 35.3756116],
      [-80.7668912, 35.3737278],
      [-80.7712356, 35.3718441],
      [-80.7755801, 35.3699604],
      [-80.7761636, 35.3697073],
      [-80.7773308, 35.3692012],
      [-80.778498, 35.3686952],
      [-80.7796651, 35.3681891],
      [-80.7808323, 35.367683],
      [-80.7812213, 35.3675143],
      [-80.7815456, 35.3673737],
      [-80.7818698, 35.3672332],
      [-80.782194, 35.3670926],
      [-80.7818049, 35.3672613],
      [-80.7811241, 35.3675565],
      [-80.7804432, 35.3678517],
      [-80.7798597, 35.3681047],
      [-80.7788384, 35.3685476],
      [-80.7778171, 35.3689904],
      [-80.7767959, 35.3694332],
      [-80.7757746, 35.369876],
      [-80.7749965, 35.3702134],
      [-80.7718192, 35.371591],
      [-80.7686419, 35.3729687],
      [-80.7654646, 35.3743464],
    ],
  },
  {
    id: "accidental",
    label: "Perpetrator enters the Yellow Zone by accident and leaves",
    title: "Perpetrator enters the Yellow Zone by accident and leaves",
    summary: "The Perpetrator clips the edge of the Yellow Zone at about 950 m, stays briefly, then moves back out past the exit buffer.",
    see: "A short burst of Yellow alerts on both watches, then a clean return to normal. The Red Zone is never involved.",
    why: "Answers the obvious client question: does a harmless accidental entry trigger an emergency? It does not.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7674099, 35.3735029],
      [-80.771495, 35.3717316],
      [-80.7755801, 35.3699604],
      [-80.7763582, 35.369623],
      [-80.7764797, 35.3695703],
      [-80.7766013, 35.3695175],
      [-80.7767229, 35.3694648],
      [-80.7768445, 35.3694121],
      [-80.7761636, 35.3697073],
      [-80.7748668, 35.3702696],
      [-80.7735699, 35.3708319],
      [-80.7722731, 35.3713942],
      [-80.7713005, 35.371816],
      [-80.7688689, 35.3728703],
      [-80.7664373, 35.3739246],
    ],
  },
  {
    id: "passby-yellow",
    label: "Perpetrator just passes by — through Yellow only",
    title: "Perpetrator just passes by",
    summary: "The Perpetrator walks a straight line past the Survivor. The closest he ever gets is about 700 m, so he crosses the Yellow Zone and comes out the other side.",
    see: "Yellow alerts while he is inside the ring, then silence once he clears the exit buffer. No Red at any point.",
    why: "Shows that passing through is treated as a warning, not an emergency.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.8018629, 35.3717782],
      [-80.8003417, 35.3717782],
      [-80.7988204, 35.3717782],
      [-80.7972992, 35.3717782],
      [-80.795778, 35.3717782],
      [-80.7942567, 35.3717782],
      [-80.7927355, 35.3717782],
      [-80.7912143, 35.3717782],
      [-80.7896931, 35.3717782],
      [-80.7881718, 35.3717782],
      [-80.7866506, 35.3717782],
      [-80.7851294, 35.3717782],
      [-80.7836082, 35.3717782],
      [-80.7820869, 35.3717782],
      [-80.7805657, 35.3717782],
      [-80.7790445, 35.3717782],
      [-80.7775233, 35.3717782],
      [-80.776002, 35.3717782],
      [-80.7744808, 35.3717782],
      [-80.7729596, 35.3717782],
      [-80.7714383, 35.3717782],
      [-80.7699171, 35.3717782],
    ],
  },
  {
    id: "passby-red",
    label: "Perpetrator passes straight through both zones and out the far side",
    title: "Perpetrator passes straight through both zones and out the far side",
    summary: "A direct transect. The Perpetrator's path cuts through the Yellow Zone, through the Red Zone, and out the other side without stopping.",
    see: "Yellow, then Red, then Yellow again, then cleared — the full escalation and step-down inside one short pass.",
    why: "The hardest sequence to stage in the field, and the one most likely to expose a state-machine bug.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.8007613, 35.367107],
      [-80.7994681, 35.367107],
      [-80.798175, 35.367107],
      [-80.7968818, 35.367107],
      [-80.7955887, 35.367107],
      [-80.7942955, 35.367107],
      [-80.7930024, 35.367107],
      [-80.7917092, 35.367107],
      [-80.790416, 35.367107],
      [-80.7891229, 35.367107],
      [-80.7878297, 35.367107],
      [-80.7865366, 35.367107],
      [-80.7852434, 35.367107],
      [-80.7839503, 35.367107],
      [-80.7826571, 35.367107],
      [-80.781364, 35.367107],
      [-80.7800708, 35.367107],
      [-80.7787776, 35.367107],
      [-80.7774845, 35.367107],
      [-80.7761913, 35.367107],
      [-80.7748982, 35.367107],
      [-80.773605, 35.367107],
      [-80.7723119, 35.367107],
      [-80.7710187, 35.367107],
    ],
  },
  {
    id: "survivor-moves",
    label: "Survivor walks towards a Perpetrator who is standing still",
    title: "Survivor walks towards a Perpetrator who is standing still",
    summary: "The Perpetrator never moves. The Survivor walks from about 2.2 km away to roughly 420 m and stops.",
    see: "Exactly the same Yellow and Red alerts as when the Perpetrator did the walking.",
    why: "The trigger is the distance between them, not who moved. Clients assume only the Perpetrator can cause an alert.",
    survivorLocations: [
      [-80.807288, 35.3562119],
      [-80.803592, 35.3578145],
      [-80.799896, 35.3594171],
      [-80.7961999, 35.3610196],
      [-80.7956164, 35.3612727],
      [-80.7946826, 35.3616775],
      [-80.7937489, 35.3620824],
      [-80.7928152, 35.3624873],
      [-80.7918814, 35.3628921],
      [-80.7909477, 35.363297],
      [-80.7905587, 35.3634657],
      [-80.7904128, 35.3635289],
      [-80.7902669, 35.3635922],
      [-80.790121, 35.3636555],
      [-80.7899751, 35.3637187],
    ],
    perpetratorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
  },
  {
    id: "mutual",
    label: "Both walk towards each other at the same time",
    title: "Both walk towards each other at the same time",
    summary: "Survivor and Perpetrator each cover half the gap, closing far faster than either could alone.",
    see: "One Yellow entry and one Red entry for the pair — not two of each, and no duplicated alerts because both people moved.",
    why: "Fastest possible closing rate. Catches double-firing and lag.",
    survivorLocations: [
      [-80.7949229, 35.3658493],
      [-80.7939682, 35.3658194],
      [-80.7930135, 35.3657894],
      [-80.7920588, 35.3657595],
      [-80.7916182, 35.3657415],
      [-80.7912547, 35.3657254],
      [-80.7908912, 35.3657092],
      [-80.7905276, 35.365693],
      [-80.7901641, 35.3656768],
      [-80.7898006, 35.3656607],
      [-80.7894371, 35.3656445],
      [-80.7890736, 35.3656283],
      [-80.78871, 35.3656122],
      [-80.7883465, 35.365596],
      [-80.787983, 35.3655798],
    ],
    perpetratorLocations: [
      [-80.7699171, 35.366568],
      [-80.7728914, 35.3663883],
      [-80.7758656, 35.3662086],
      [-80.7788399, 35.366029],
      [-80.7792805, 35.3659841],
      [-80.7796661, 35.3659526],
      [-80.7800516, 35.3659212],
      [-80.7804372, 35.3658897],
      [-80.7808227, 35.3658583],
      [-80.7812083, 35.3658269],
      [-80.7815938, 35.3657954],
      [-80.7819794, 35.365764],
      [-80.7823649, 35.3657325],
      [-80.7827505, 35.3657011],
      [-80.7831361, 35.3656697],
    ],
  },
  {
    id: "constant-gap",
    label: "Both walking the same way, holding an 800 m gap inside Yellow",
    title: "Both walking the same way, holding an 800 m gap inside Yellow",
    summary: "Both people walk in the same direction at the same speed. The gap between them never changes and never drops below 800 m.",
    see: "Yellow alerts keep repeating on every check-in even though the closing rate reads about zero.",
    why: "Confirms the zone drives the alert. 'Nobody is getting closer' must not silence an active Yellow.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.7852195, 35.3656853],
      [-80.7845489, 35.3658806],
      [-80.7838784, 35.3660759],
      [-80.7832079, 35.3662711],
      [-80.7825374, 35.3664664],
      [-80.7818668, 35.3666617],
      [-80.7811963, 35.366857],
      [-80.7805258, 35.3670523],
      [-80.7798553, 35.3672476],
      [-80.7791847, 35.3674429],
      [-80.7785142, 35.3676381],
      [-80.7778437, 35.3678334],
      [-80.7771732, 35.3680287],
      [-80.7765026, 35.368224],
      [-80.7758321, 35.3684193],
      [-80.7751616, 35.3686146],
      [-80.7744911, 35.3688098],
      [-80.7738205, 35.3690051],
      [-80.77315, 35.3692004],
      [-80.7724795, 35.3693957],
      [-80.771809, 35.369591],
      [-80.7711384, 35.3697863],
      [-80.7704679, 35.3699816],
    ],
    perpetratorLocations: [
      [-80.7771875, 35.3666578],
      [-80.776517, 35.3668531],
      [-80.7758465, 35.3670484],
      [-80.775176, 35.3672437],
      [-80.7745054, 35.3674389],
      [-80.7738349, 35.3676342],
      [-80.7731644, 35.3678295],
      [-80.7724939, 35.3680248],
      [-80.7718233, 35.3682201],
      [-80.7711528, 35.3684154],
      [-80.7704823, 35.3686107],
      [-80.7698117, 35.3688059],
      [-80.7691412, 35.3690012],
      [-80.7684707, 35.3691965],
      [-80.7678002, 35.3693918],
      [-80.7671296, 35.3695871],
      [-80.7664591, 35.3697824],
      [-80.7657886, 35.3699777],
      [-80.7651181, 35.3701729],
      [-80.7644475, 35.3703682],
      [-80.763777, 35.3705635],
      [-80.7631065, 35.3707588],
      [-80.762436, 35.3709541],
      [-80.7617654, 35.3711494],
    ],
  },
  {
    id: "orbit",
    label: "Perpetrator loitering — circling at 700 m without entering Red",
    title: "Perpetrator loitering",
    summary: "The Perpetrator circles the Survivor at a steady 700 m for a full loop.",
    see: "Continuous Yellow, repeating on every 20-second check-in, with no Red the whole time.",
    why: "Loitering is a real pattern. It should stay a sustained warning, not escalate on its own.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.778644, 35.3676407],
      [-80.7793803, 35.3688607],
      [-80.7804211, 35.369923],
      [-80.7817175, 35.3707781],
      [-80.7832091, 35.3713859],
      [-80.784826, 35.371718],
      [-80.7864927, 35.3717589],
      [-80.7881312, 35.3715067],
      [-80.7896649, 35.3709732],
      [-80.791022, 35.3701832],
      [-80.7921393, 35.3691738],
      [-80.7929643, 35.3679922],
      [-80.7934585, 35.3666936],
      [-80.7935988, 35.3653386],
      [-80.7933787, 35.3639908],
      [-80.7928084, 35.3627131],
      [-80.7919146, 35.3615652],
      [-80.7907391, 35.3606008],
      [-80.7893369, 35.359865],
      [-80.7877735, 35.3593923],
      [-80.786122, 35.3592047],
      [-80.7844597, 35.3593109],
      [-80.7828642, 35.3597061],
      [-80.7814103, 35.3603718],
      [-80.7801658, 35.3612768],
      [-80.779189, 35.3623787],
      [-80.7785255, 35.3636262],
      [-80.7782063, 35.3649608],
      [-80.7782464, 35.3663202],
      [-80.778644, 35.3676407],
    ],
  },
  {
    id: "jitter",
    label: "Perpetrator hovering on the Yellow line with GPS wobble",
    title: "Perpetrator hovering on the Yellow line with GPS wobble",
    summary: "The Perpetrator sits right on the 1,000 m boundary. Readings bounce between about 985 m and 1,015 m.",
    see: "One clean Yellow entry, then a stable Yellow. The alerts and check-in speed must not flip back and forth on every reading.",
    why: "Without the exit buffer this becomes an alert storm and the Advocate stops trusting the system.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7744336, 35.36549],
      [-80.7748222, 35.3665783],
      [-80.7753184, 35.3675997],
      [-80.7755185, 35.3686735],
      [-80.7761972, 35.369605],
      [-80.776671, 35.3706333],
      [-80.7777325, 35.3713245],
      [-80.7784712, 35.3722394],
      [-80.7796038, 35.3728121],
      [-80.7806408, 35.3734999],
      [-80.7819383, 35.3737789],
      [-80.7831343, 35.3742617],
      [-80.784459, 35.3743608],
      [-80.7857695, 35.3745894],
      [-80.7870798, 35.3743392],
      [-80.7884028, 35.3742732],
    ],
  },
  {
    id: "buffer",
    label: "Perpetrator steps just outside Yellow — still inside the exit buffer",
    title: "Perpetrator steps just outside Yellow",
    summary: "From 900 m the Perpetrator drifts out to about 1,020 m, which is past the ring but inside the 50 m exit buffer, then comes back in to 880 m.",
    see: "The pair stays in Yellow the whole time. This is not counted as leaving and the return is not a brand-new entry.",
    why: "Shows the difference between a real exit and a boundary drift. The 50 m buffer is still an assumption, not a Leeb-signed number.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7726711, 35.36549],
      [-80.773993, 35.3655978],
      [-80.7759758, 35.3657056],
      [-80.7753148, 35.3658134],
      [-80.7746539, 35.3659212],
      [-80.7745437, 35.366029],
      [-80.7750945, 35.3661368],
      [-80.7761961, 35.3662446],
      [-80.7765266, 35.3663524],
    ],
  },
  {
    id: "deescalate",
    label: "Perpetrator eases from Red back into Yellow",
    title: "Perpetrator eases from Red back into Yellow",
    summary: "From 400 m inside the Red Zone the Perpetrator backs off to about 700 m, staying inside the Yellow Zone.",
    see: "Red alerts stop and Yellow alerts resume. This is a step down, not an all-clear — check-in speed goes from 10 seconds to 20 seconds, not back to 30 seconds.",
    why: "Advocates need to see the difference between 'backed off a bit' and 'gone'.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7819995, 35.3671769],
      [-80.7819022, 35.3672191],
      [-80.7818049, 35.3672613],
      [-80.7817077, 35.3673034],
      [-80.7813186, 35.3674721],
      [-80.7808712, 35.3676661],
      [-80.7804238, 35.3678601],
      [-80.7799764, 35.3680541],
      [-80.779529, 35.3682481],
      [-80.7790815, 35.3684421],
      [-80.7789843, 35.3684843],
      [-80.7787573, 35.3685827],
      [-80.7785304, 35.3686811],
      [-80.7783034, 35.3687795],
    ],
  },
  {
    id: "sparse-red",
    label: "First location we ever receive is already inside Red",
    title: "First location we ever receive is already inside Red",
    summary: "The watch was out of coverage. The first usable fix after it reconnects puts the Perpetrator at 430 m — we never saw him cross the Yellow ring.",
    see: "Treated as Red immediately. It is not ignored just because no Yellow reading was ever recorded.",
    why: "Real GPS is not continuous. The system must classify what it has, not what it missed.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7817077, 35.3673034],
      [-80.7817784, 35.3672728],
      [-80.7818491, 35.3672421],
      [-80.7819199, 35.3672114],
      [-80.7819906, 35.3671808],
      [-80.7820614, 35.3671501],
      [-80.7821321, 35.3671194],
      [-80.7822028, 35.3670887],
      [-80.7822736, 35.3670581],
      [-80.7823443, 35.3670274],
      [-80.782415, 35.3669967],
      [-80.7824858, 35.3669661],
    ],
  },
  {
    id: "stale-red",
    label: "GPS goes bad while the Perpetrator is inside the Red Zone",
    title: "GPS goes bad while the Perpetrator is inside the Red Zone",
    summary: "The Perpetrator closes into the Red Zone normally, then his fixes turn stale and low accuracy for several check-ins before recovering.",
    see: "Red is held on the last trusted position. The marker stops moving and fades, the alert does not clear on bad data, and no confident new distance is drawn.",
    why: "Fail-safe behaviour. Clearing an emergency because the GPS got noisy is the worst possible failure.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7713005, 35.371816],
      [-80.7735375, 35.370846],
      [-80.7757746, 35.369876],
      [-80.7765527, 35.3695386],
      [-80.7779792, 35.3689201],
      [-80.7794058, 35.3683015],
      [-80.7808323, 35.367683],
      [-80.7812213, 35.3675143],
      [-80.7814645, 35.3674089],
      [-80.7817077, 35.3673034],
      [-80.7817077, 35.3673034],
      [-80.7817077, 35.3673034],
      [-80.7817077, 35.3673034],
      [-80.7817077, 35.3673034],
      [-80.7817077, 35.3673034],
      [-80.7817077, 35.3673034],
      [-80.7829721, 35.3667552],
      [-80.7831018, 35.366699],
      [-80.7832315, 35.3666427],
      [-80.7833611, 35.3665865],
    ],
  },
  {
    id: "missed-graze",
    label: "Perpetrator crosses both zones between two regular check-ins",
    title: "Perpetrator crosses both zones between two regular check-ins",
    summary: "While the pair is far apart the watches report only every 30 seconds. The Perpetrator drives through the Yellow Zone and the Red Zone and is gone again before the next report.",
    see: "The reported positions never land inside a zone, so no alert fires. The grey line shows the movement the system never saw.",
    why: "This is an open product decision — whether closing rate should speed up reporting before Yellow. Do not record it as a pass or a bug until Leeb rules on it.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.7601152, 35.3766659],
      [-80.771495, 35.3717316],
      [-80.8007613, 35.3590671],
      [-80.814531, 35.3530933],
    ],
  },
  {
    id: "inactive",
    label: "Pair is set to Inactive — Perpetrator walks in anyway",
    title: "Pair is set to Inactive",
    summary: "The same close approach, but the Threat Pair has been marked Inactive by the Advocate.",
    see: "No alerts, no faster check-ins, no rings and no pair overlay. The watches keep their normal 30-second reporting.",
    why: "Confirms that switching a pair off actually stops the monitoring behaviour, not just the screen.",
    survivorLocations: [
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
      [-80.78589, 35.36549],
    ],
    perpetratorLocations: [
      [-80.764492, 35.3747681],
      [-80.7680583, 35.3732217],
      [-80.7716247, 35.3716754],
      [-80.775191, 35.370129],
      [-80.7761636, 35.3697073],
      [-80.7771363, 35.3692856],
      [-80.7781089, 35.3688639],
      [-80.7790815, 35.3684421],
      [-80.7800542, 35.3680204],
      [-80.7810268, 35.3675987],
      [-80.7819995, 35.3671769],
      [-80.7829721, 35.3667552],
    ],
  },
];
